<template>
  <div class="modal fade" id="addWaterMaskModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ isEdit ? 'Edit Water Mask' : 'New Water Mask' }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <!-- Edit-mode area picker: lets the user switch the edit target -->
          <!-- without leaving the modal. -->
          <div v-if="isEdit && areas && areas.length" class="row mb-3">
            <div class="col">
              <label class="form-label">Area to edit</label>
              <select class="form-select" v-model="selectedAreaId" @change="onPickArea">
                <option v-for="a in areas" :key="a.id" :value="a.id">
                  {{ a.name }}{{ a.has_water_geojson ? '' : ' (read-only — legacy)' }}
                </option>
              </select>
            </div>
          </div>

          <form @submit.prevent="save">
            <div class="row mb-2">
              <div class="col">
                <label class="form-label">Name</label>
                <input v-model="form.name" type="text" class="form-control" :disabled="isReadOnly" required>
              </div>
            </div>
            <div class="row mb-2">
              <div class="col">
                <label class="form-label">Description</label>
                <input v-model="form.description" type="text" class="form-control" :disabled="isReadOnly">
              </div>
            </div>

            <!-- Previously uploaded files (history). Two operations only: -->
            <!-- "Clear all" wipes the list AND the inline water_geojson on -->
            <!-- save; new files can be added via the picker below. -->
            <div v-if="isEdit && (form.keptFiles.length || form.clearAll)" class="row mb-2">
              <div class="col">
                <label class="form-label d-flex justify-content-between align-items-center">
                  <span>Previously uploaded files</span>
                  <button
                    v-if="!isReadOnly && !form.clearAll && form.keptFiles.length"
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    @click="clearAllFiles"
                  >
                    Clear all files
                  </button>
                  <button
                    v-else-if="!isReadOnly && form.clearAll"
                    type="button"
                    class="btn btn-sm btn-outline-secondary"
                    @click="undoClearAll"
                  >
                    Undo clear
                  </button>
                </label>
                <ul v-if="!form.clearAll" class="list-group list-group-flush">
                  <li
                    v-for="(f, idx) in form.keptFiles"
                    :key="`kept-${idx}-${f.name}`"
                    class="list-group-item d-flex align-items-center py-1 px-2"
                  >
                    <span class="badge bg-secondary me-2">{{ f.kind || 'water' }}</span>
                    {{ f.name }}
                    <span class="text-muted ms-2">{{ humanSize(f.size || 0) }}</span>
                  </li>
                </ul>
                <div v-else class="alert alert-warning py-2 mb-0">
                  All files will be removed and the inline water mask will be wiped on save. Add new files below to replace the mask, or save as-is to leave the area without a mask.
                </div>
              </div>
            </div>

            <div class="row mb-2">
              <div class="col">
                <label class="form-label">
                  {{ isEdit ? 'Add more shapefiles (optional)' : 'Shapefiles (.shp + .dbf + .shx, optional .prj)' }}
                </label>
                <div class="input-group">
                  <input
                    ref="fileInput"
                    type="file"
                    class="form-control"
                    multiple
                    accept=".shp,.dbf,.shx,.prj,.cpg"
                    :disabled="isReadOnly"
                    @change="onFilesChange"
                  >
                  <button
                    type="button"
                    class="btn btn-outline-secondary"
                    :disabled="!form.files.length || isReadOnly"
                    @click="clearFiles"
                  >
                    Clear
                  </button>
                </div>
                <div class="form-text">
                  Pick from one folder, then click again to add files from another folder — selections are accumulated.
                </div>
                <div v-if="form.files.length" class="form-text">
                  {{ form.files.length }} new file(s) selected ({{ humanSize(totalSize) }})
                </div>
                <div v-if="totalSize > MAX_BYTES" class="form-text text-danger">
                  Total size exceeds 100 MB limit.
                </div>
                <div v-if="isEdit && !form.files.length" class="form-text">
                  No new files — existing water mask will be kept.
                </div>
              </div>
            </div>
            <div class="row mb-2">
              <div class="col">
                <label class="form-label">Area polygon</label>
                <div class="d-flex align-items-center">
                  <button
                    type="button"
                    class="btn btn-outline-primary btn-sm me-2"
                    :disabled="isReadOnly"
                    @click="pickPolygon"
                  >
                    {{ form.polygon.length ? 'Re-pick polygon' : 'Pick polygon on map' }}
                  </button>
                  <span v-if="form.polygon.length" class="form-text text-success ms-1">
                    {{ form.polygon.length }} point(s) selected
                  </span>
                  <span v-else class="form-text ms-1">
                    No polygon yet
                  </span>
                </div>
                <div class="form-text">
                  Click on the map to add points; double-click to finish.
                </div>
              </div>
            </div>
            <div v-if="errorMessage" class="alert alert-danger py-2 mb-0">
              {{ errorMessage }}
            </div>
            <div v-if="isEdit && isLegacy" class="alert alert-warning py-2 mb-0 mt-2">
              This is a legacy file-based area. Editing is disabled; you can only Preview or Delete it.
            </div>
            <div v-else-if="isEdit && !canEditSelected" class="alert alert-warning py-2 mb-0 mt-2">
              This area was created by another user. Only its owner or an admin can edit it.
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <!-- Edit-only actions on the left -->
          <div class="me-auto" v-if="isEdit">
            <button
              type="button"
              class="btn btn-outline-info me-2"
              @click="preview"
            >
              <span v-if="isPreviewing" class="spinner-border spinner-border-sm me-1"></span>
              Preview on map
            </button>
            <button
              v-if="isAdmin"
              type="button"
              class="btn btn-outline-danger"
              :disabled="isDeleting"
              @click="confirmDelete"
            >
              <span v-if="isDeleting" class="spinner-border spinner-border-sm me-1"></span>
              Delete
            </button>
          </div>
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="!canSave || isSaving || isReadOnly"
            @click="save"
          >
            <span v-if="isSaving" class="spinner-border spinner-border-sm me-1"></span>
            Save
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from 'bootstrap';
import axios from 'axios';

const MAX_BYTES = 100 * 1024 * 1024;

// Identify the same file across multiple selections so duplicates are skipped.
function fileKey(f) {
  return `${f.name}|${f.size}|${f.lastModified}`;
}

export default {
  name: 'AddWaterMaskModal',
  emits: ['saved', 'deleted', 'preview', 'request-polygon'],
  data() {
    return {
      form: this.initForm(),
      areas: [],
      selectedAreaId: null,
      editingId: null,
      isLegacy: false,
      // Отдаётся бэкендом (/areas → can_edit): admin правит всё,
      // operator — только созданные им области.
      canEditSelected: true,
      modal: null,
      isSaving: false,
      isDeleting: false,
      isPreviewing: false,
      errorMessage: '',
      MAX_BYTES,
    };
  },
  computed: {
    isEdit() {
      // Edit mode is determined by whether a list of areas was passed in
      // (i.e. the user opened the dialog via the Edit button), not by
      // whether a specific area is currently picked.
      return this.areas.length > 0;
    },
    isReadOnly() {
      return this.isEdit && (this.isLegacy || !this.canEditSelected);
    },
    isAdmin() {
      return this.$store.getters.isAdmin;
    },
    totalSize() {
      return this.form.files.reduce((acc, f) => acc + f.size, 0);
    },
    canSave() {
      const filesOk = this.editingId ? true : this.form.files.length > 0;
      return (
        !this.isReadOnly
        && this.form.name.trim().length > 0
        && filesOk
        && this.form.polygon.length >= 3
        && this.totalSize <= MAX_BYTES
      );
    },
  },
  mounted() {
    this.modal = new Modal(document.getElementById('addWaterMaskModal'));
  },
  methods: {
    initForm() {
      return {
        name: '',
        description: '',
        files: [],
        keptFiles: [],
        clearAll: false,
        polygon: [],
      };
    },
    humanSize(bytes) {
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    },
    open(area, areas) {
      // Two open modes:
      //   open()                — create new
      //   open(area, areasList) — edit. `area` may be null (then we pick the
      //                            first area from the list); `areasList` is
      //                            the dropdown source.
      this.form = this.initForm();
      this.errorMessage = '';
      this.editingId = null;
      this.selectedAreaId = null;
      this.isLegacy = false;
      this.canEditSelected = true;
      this.areas = Array.isArray(areas) ? areas.slice() : [];

      const initialArea = area || (this.areas.length ? this.areas[0] : null);
      if (initialArea) {
        this.loadAreaIntoForm(initialArea);
      }
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
      this.modal.show();
    },
    loadAreaIntoForm(area) {
      this.editingId = area.id;
      this.selectedAreaId = area.id;
      this.isLegacy = !area.has_water_geojson;
      // Старые ответы без can_edit трактуем как «можно» — бэкенд всё равно проверит.
      this.canEditSelected = area.can_edit !== false;
      this.form.name = area.name || '';
      this.form.description = area.description || '';
      this.form.files = [];
      this.form.keptFiles = Array.isArray(area.source_files) ? area.source_files.slice() : [];
      this.form.clearAll = false;
      // GeoJSON polygon: coordinates is [[ [lon,lat], ... ]]; drop the
      // closing duplicate vertex so re-saves don't grow the ring.
      const ring = area.poly?.coordinates?.[0] || [];
      const open = ring.length > 1
        && ring[0][0] === ring[ring.length - 1][0]
        && ring[0][1] === ring[ring.length - 1][1]
        ? ring.slice(0, -1)
        : ring;
      this.form.polygon = open.map((p) => [p[0], p[1]]);
      this.errorMessage = '';
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },
    onPickArea() {
      const picked = this.areas.find((a) => a.id === this.selectedAreaId);
      if (picked) this.loadAreaIntoForm(picked);
    },
    close() {
      this.modal.hide();
    },
    onFilesChange(event) {
      // Native <input multiple> only allows picking from one directory at a
      // time, so accumulate across multiple selections rather than replacing.
      const incoming = Array.from(event.target.files || []);
      const seen = new Set(this.form.files.map(fileKey));
      for (const f of incoming) {
        if (!seen.has(fileKey(f))) {
          this.form.files.push(f);
          seen.add(fileKey(f));
        }
      }
      // Reset the input so re-selecting the same files in another folder fires
      // a fresh `change` event.
      event.target.value = '';
    },
    clearFiles() {
      this.form.files = [];
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },
    clearAllFiles() {
      // Mark the area's mask + history for full wipe on save. The actual
      // keptFiles list is left in memory so "Undo clear" can restore the
      // visible state without a re-fetch.
      this.form.clearAll = true;
    },
    undoClearAll() {
      this.form.clearAll = false;
    },
    pickPolygon() {
      // Hide modal so the user can interact with the map; parent listens for
      // 'request-polygon' and will call setPolygon() when drawing finishes.
      this.modal.hide();
      this.$emit('request-polygon');
    },
    setPolygon(coords) {
      this.form.polygon = coords || [];
      this.modal.show();
    },
    async preview() {
      if (!this.editingId) return;
      this.isPreviewing = true;
      try {
        // Если в форме есть несохранённые правки (polygon / files / name),
        // сначала сохраняем — иначе /areas/{id}/preview вернёт старое
        // состояние с сервера и пользователь увидит устаревшую маску.
        if (this.canSave && !this.isReadOnly) {
          await this.save();
          // save() сам закроет модалку и эмитнет 'saved'. Если сохранение
          // упало, errorMessage уже выставлен — preview не показываем.
          if (this.errorMessage) return;
        }
        this.modal.hide();
        this.$emit('preview', { id: this.editingId });
      } finally {
        setTimeout(() => { this.isPreviewing = false; }, 500);
      }
    },
    async confirmDelete() {
      if (!this.editingId) return;
      const picked = this.areas.find((a) => a.id === this.editingId);
      const name = picked ? picked.name : this.editingId;
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Delete area "${name}"? This cannot be undone.`)) return;
      this.isDeleting = true;
      this.errorMessage = '';
      try {
        await axios.delete(`/areas/${this.editingId}`);
        const deletedId = this.editingId;
        // Remove from local list and either pick the next area or close.
        this.areas = this.areas.filter((a) => a.id !== deletedId);
        this.$emit('deleted', { id: deletedId });
        if (this.areas.length) {
          this.loadAreaIntoForm(this.areas[0]);
        } else {
          this.close();
        }
      } catch (err) {
        this.errorMessage = err?.response?.data?.detail || err.message || 'Failed to delete area';
      } finally {
        this.isDeleting = false;
      }
    },
    async save() {
      if (!this.canSave) return;
      this.isSaving = true;
      this.errorMessage = '';
      try {
        const fd = new FormData();
        fd.append('name', this.form.name.trim());
        fd.append('description', this.form.description || '');
        fd.append('polygon', JSON.stringify(this.form.polygon));
        for (const file of this.form.files) {
          fd.append('files', file, file.name);
        }
        const isEditingExisting = !!this.editingId;
        if (isEditingExisting && this.form.clearAll) {
          // Backend wipes water_geojson + source_files. If new files are
          // also attached they replace the mask in a single round-trip.
          fd.append('clear_water_mask', 'true');
        }
        const url = isEditingExisting ? `/areas/${this.editingId}` : '/areas';
        const method = isEditingExisting ? 'put' : 'post';
        const { data } = await axios({
          url,
          method,
          data: fd,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        this.$emit('saved', data);
        this.close();
      } catch (err) {
        this.errorMessage = err?.response?.data?.detail || err.message || 'Failed to save area';
      } finally {
        this.isSaving = false;
      }
    },
    reopen() {
      // Used by the parent to bring the modal back after a preview session.
      this.modal.show();
    },
  },
};
</script>
